import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { SegmentedFilter } from "@/components/ui/segmented-filter";

import { UnpaidSessionsTab } from "@/components/admin/payouts/UnpaidSessionsTab";
import { PayoutHistoryTab } from "@/components/admin/payouts/PayoutHistoryTab";
import { CreatePayoutDialog } from "@/components/admin/payouts/CreatePayoutDialog";
import { PayoutRequestsSection } from "@/components/admin/payouts/PayoutRequestsSection";
import { groupSessionsByTherapist } from "@/components/admin/payouts/payout-helpers";

import type { UnpaidSession } from "@/types";
import type { WithholdingRates } from "@/lib/money/work-contract";

export const Route = createFileRoute("/dashboard/admin/payouts/")({
  component: AdminPayouts,
});

type Tab = "unpaid" | "history";

const TAB_OPTIONS: { value: Tab; label: string }[] = [
  { value: "unpaid", label: "Neisplaćene sesije" },
  { value: "history", label: "Historija isplata" },
];

function AdminPayouts() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("unpaid");

  /* Dialog state */
  const [payoutDialogSessions, setPayoutDialogSessions] = useState<
    UnpaidSession[] | null
  >(null);
  const [payoutNote, setPayoutNote] = useState("");
  const [payoutRates, setPayoutRates] = useState<WithholdingRates | null>(null);
  const [payoutVersion, setPayoutVersion] = useState(0);

  /* ─── Mutations ─── */

  // The backend creates one payout per therapist, so a selection spanning
  // several therapists is split into one request per therapist group.
  const createPayoutMutation = useMutation({
    mutationFn: async (data: { sessions: UnpaidSession[]; note: string }) => {
      const groups = groupSessionsByTherapist(data.sessions);
      for (const group of groups) {
        const res = await fetch("/api/admin/payouts", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionIds: group.sessions.filter((s) => s.kind !== "enrollment").map((s) => s.id),
            enrollmentIds: group.sessions.filter((s) => s.kind === "enrollment").map((s) => s.id),
            note: data.note,
          }),
        });
        await throwIfNotOk(res, "Greška pri kreiranju isplate");
      }
      return groups.length;
    },
    onSuccess: (count) => {
      toast.success(
        count === 1
          ? "Isplata uspješno kreirana"
          : `Uspješno kreirane isplate: ${count}`,
      );
      setPayoutDialogSessions(null);
      setPayoutNote("");
      setPayoutVersion((v) => v + 1);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "payouts"] });
    },
  });

  /* ─── Helpers ─── */

  const openPayoutDialog = (sessions: UnpaidSession[], rates: WithholdingRates | null) => {
    setPayoutDialogSessions(sessions);
    setPayoutRates(rates);
    setPayoutNote("");
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6 pb-14">
      {/* Header */}
      <header className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
            style={{ fontFamily: "var(--font-heading)" }}
          >
            Isplate terapeutima
          </h1>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Pregled neisplaćenih sesija i grupnih terapija i historija isplata.
          </p>
        </div>

        <SegmentedFilter
          ariaLabel="Prikaz isplata"
          options={TAB_OPTIONS}
          value={tab}
          onChange={setTab}
        />
      </header>

      <PayoutRequestsSection />

      {/* Tab content */}
      {tab === "unpaid" && (
        <UnpaidSessionsTab
          key={payoutVersion}
          onOpenPayoutDialog={openPayoutDialog}
        />
      )}

      {tab === "history" && <PayoutHistoryTab />}

      {/* Payout Confirmation Dialog */}
      {payoutDialogSessions && (
        <CreatePayoutDialog
          sessions={payoutDialogSessions}
          withholdingRates={payoutRates}
          note={payoutNote}
          onNoteChange={setPayoutNote}
          isPending={createPayoutMutation.isPending}
          onConfirm={() =>
            createPayoutMutation.mutate({
              sessions: payoutDialogSessions,
              note: payoutNote,
            })
          }
          onClose={() => {
            setPayoutDialogSessions(null);
            setPayoutNote("");
          }}
        />
      )}
    </div>
  );
}
